'use client'

import { Button } from '@/components/Ui'
import OfficeFragment from '@/lib/fragments/office'

import { Parse } from '@/components/Core'
import { formatAddress } from '@/lib/text'
import { useStoreCursor } from '@/store'
import { Dato, Lib } from '@/types'
import { useTranslations } from 'next-intl'
import { HTMLAttributes } from 'react'

interface CardOfficeData extends Lib.FragmentOf<typeof OfficeFragment> {
  address: Dato.DatoAddress['address']
}

export interface CardOfficeProps extends HTMLAttributes<HTMLDivElement> {
  data: CardOfficeData
}

const CardOffice = ({ data, ...props }: CardOfficeProps) => {
  const { setHoverDefault } = useStoreCursor()

  const { line1, line2 } = formatAddress(data.address.formatted_address)
  const t = useTranslations()
  return (
    <div
      {...props}
      className="gap-gutter grid cursor-pointer py-2 md:grid-cols-2"
      onMouseLeave={() => setHoverDefault(false)}
      onMouseEnter={() => setHoverDefault(true)}
    >
      <h3 className="md:col-span-2">{data.title}</h3>
      <div>
        <div className="prose prose-a:font-normal prose-a:hover:no-underline">
          {line1 && <div>{line1}</div>}
          {line2 && <div>{line2}</div>}
          {data.telephone && (
            <div>
              <a href={`tel:${data.telephone}`}>{`T ${data.telephone}`}</a>
            </div>
          )}
          {data.email && (
            <div className="text-style-underline">
              <a href={`mailto:${data.email}`}>{`${data.email}`}</a>
            </div>
          )}
        </div>

        {data?.googleDirectionsLink && (
          <Button
            variant="text"
            isNext={false}
            icon="lucide:arrow-up-right"
            target="_blank"
            href={data.googleDirectionsLink}
          >
            {t('section_offices_google_link')}
          </Button>
        )}
      </div>
      {(data.openings?.title || data.openings?.content) && (
        <div className="gap-sm flex flex-col items-start justify-end">
          <p className="text-base font-bold">{data.openings?.title}</p>
          <Parse html={data.openings?.content} />
        </div>
      )}
    </div>
  )
}

export default CardOffice
